/**
 * Shared maths for per-coil inventory.
 *
 * WEIGHT IS AUTHORITATIVE. A coil is bought, billed and stocked in kg; its length is a
 * derived attribute shown while picking, computed from that coil's own kg-per-metre
 * (coils of the same product size differ from each other, so there is no global
 * weight-per-metre to use here).
 *
 * Weights are WHOLE KG — the database enforces it — so anything this file returns as a
 * weight is rounded to an integer. Lengths are 3-dp.
 */

/** Parse anything the API or a form input hands us into a finite number, else 0. */
function toNumber(value) {
  const num = parseFloat(value);
  return Number.isFinite(num) ? num : 0;
}

/** Round to 3 decimal places — the precision lengths are stored at. */
function round3(value) {
  return Math.round(value * 1000) / 1000;
}

/**
 * Kilograms per metre for one coil.
 *
 * Prefers the server-computed kg_per_m and falls back to the coil's own original
 * weight/length, so a coil created locally (not yet round-tripped through the API)
 * still measures correctly.
 *
 * @param {Object} coil
 * @returns {number} kg per metre, or 0 when the coil carries no usable length
 */
export function kgPerMetre(coil) {
  if (!coil) return 0;

  const stored = toNumber(coil.kg_per_m);
  if (stored > 0) return stored;

  const originalLength = toNumber(coil.original_length_m);
  if (originalLength <= 0) return 0;

  return round3(toNumber(coil.original_weight_kg) / originalLength);
}

/**
 * How many metres a given number of kilograms represents on this coil.
 *
 * @param {Object} coil
 * @param {number|string} kg
 * @returns {number} metres, 3 dp (0 when the coil has no kg-per-metre to divide by)
 */
export function metresForKg(coil, kg) {
  const perMetre = kgPerMetre(coil);
  if (perMetre <= 0) return 0;

  return round3(toNumber(kg) / perMetre);
}

/**
 * What is left of a coil after taking `takeKg` off it.
 *
 * Based on remaining_weight_kg — the coil's physical remainder — not
 * available_weight_kg, which is that same remainder minus what other orders are
 * holding. The remainder keeps the coil's identity (same batch, same kg-per-metre),
 * so its length is re-derived rather than subtracted.
 *
 * Clamped at zero: a take larger than the coil is a validation error for the caller to
 * report (errors.takeExceedsAvailable), not a negative coil to render.
 *
 * @param {Object} coil
 * @param {number|string} takeKg
 * @returns {{weightKg: number, lengthM: number}} whole kg, metres at 3 dp
 */
export function remainderAfterTake(coil, takeKg) {
  const remaining = coil && coil.remaining_weight_kg !== undefined && coil.remaining_weight_kg !== null
    ? toNumber(coil.remaining_weight_kg)
    : toNumber(coil?.original_weight_kg);

  const weightKg = Math.max(0, Math.round(remaining - toNumber(takeKg)));

  return { weightKg, lengthM: metresForKg(coil, weightKg) };
}

/**
 * Render a weight. Whole kg, so "300" — never "300.000".
 * Returns '' for a non-numeric value; the caller decides whether that shows as a dash.
 *
 * @param {number|string} value
 * @returns {string}
 */
export function formatKg(value) {
  const num = parseFloat(value);
  if (!Number.isFinite(num)) return '';

  return String(Math.round(num));
}

/**
 * Render a length. One decimal place is what the shop floor reads off a coil label;
 * the stored 3-dp value stays intact underneath.
 *
 * @param {number|string} value
 * @returns {string}
 */
export function formatMetres(value) {
  const num = parseFloat(value);
  if (!Number.isFinite(num)) return '';

  return num.toFixed(1);
}

/**
 * Split a weighbridge net weight into "already individually weighed" and "still to be
 * pro-rated", for a document that mixes coil lines with ordinary lines.
 *
 * Every coil on a line was weighed one at a time before it went on the truck, so its
 * weight is a fact, not an estimate. Pro-rating the truck's net weight across coil
 * lines as well would overwrite those facts with a share-of-the-total guess — and then
 * the coil ledger and the line quantity disagree for the same physical coil. So coil
 * lines are subtracted out first and only the ordinary lines divide what remains.
 *
 * KEEP THIS IN ONE PLACE. The purchase-unloading screen and the truck-loading screen
 * both need it, and if a third caller ever writes its own "subtract the known weights,
 * prorate the rest" the two copies WILL drift — one rounds each share, the other rounds
 * the running total; one clamps a negative remainder, the other does not. The symptom
 * is not a crash: it is stock that is off by a few kg per truck, in a direction nobody
 * can reproduce, discovered at a quarterly stock-take months after the change that
 * caused it.
 *
 * @param {Object} args
 * @param {Array} args.items                      All lines on the document
 * @param {number|string} args.netWeight          Weighbridge net (loaded − empty), kg
 * @param {(item: Object, index: number) => boolean} args.isCoilLine  True for coil-tracked lines
 * @param {(item: Object, index: number) => number} args.weightOf     Picked kg for a coil line
 * @returns {{prorataNet: number, coilTotal: number}}
 *          coilTotal  — sum of the coil lines' weighed kg
 *          prorataNet — what the non-coil lines share, clamped at 0
 */
export function splitCoilAndProrataWeights({ items, netWeight, isCoilLine, weightOf } = {}) {
  const lines = Array.isArray(items) ? items : [];
  const isCoil = typeof isCoilLine === 'function' ? isCoilLine : () => false;
  const weigh = typeof weightOf === 'function' ? weightOf : () => 0;

  const coilTotal = lines.reduce((sum, item, index) => (
    isCoil(item, index) ? sum + Math.round(toNumber(weigh(item, index))) : sum
  ), 0);

  // Clamped: a mistyped tare can make the coil weights exceed the net, and a negative
  // pro-rata pool would hand every ordinary line a negative quantity.
  //
  // The net is NOT rounded here. Rounding it shifted a kilogram of billed weight between
  // ordinary lines on trucks with no coils on them at all — the callers' own
  // largest-remainder pass is what makes the parts whole, and it needs the unrounded pool
  // to land the remainder where it belongs. Coil weights are whole kg by DB constraint,
  // so subtracting them changes nothing about that.
  const prorataNet = Math.max(0, toNumber(netWeight) - coilTotal);

  return { prorataNet, coilTotal };
}
